import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/auth';
import { UserRole } from '@/context/auth/types';
import { 
  checkSmsStatus, 
  enableSmsFeature, 
  disableSmsFeature, 
  getSmsUsage 
} from '@/services/smsApi';

// Define the context type
interface SmsBillingContextType {
  smsEnabled: boolean;
  usageData: any;
  loading: boolean;
  error: string | null;
  organisationId?: string;
  enableSms: () => Promise<void>;
  disableSms: () => Promise<void>;
  refreshData: () => Promise<void>;
}

// Create the context with undefined initial value
const SmsBillingContext = createContext<SmsBillingContextType | undefined>(undefined);

import { cachedRequest, invalidateCache, invalidateCacheByPrefix } from '@/utils/requestCache';

// Global variable to track the last fetch time to prevent duplicate calls
let globalLastFetchTime = 0;
// Global flag to track if a fetch is in progress
let isFetchingStatus = false;

// Helper function to read cached SMS status 
const getCachedSmsStatus = (): boolean | null => {
  try {
    const cachedData = localStorage.getItem('sms_status_cache');
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      // Only use cache if it's not too old (less than 5 minutes)
      if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
        console.log('Using cached SMS status for initial render:', parsed.enabled);
        return parsed.enabled;
      }
    }
    return null;
  } catch (e) {
    console.warn('Error reading SMS status from cache:', e);
    return null;
  }
};

export const SmsBillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, dataInitialized } = useAuth();
  
  // Try to get initial state from cache to prevent flashing
  const cachedStatus = getCachedSmsStatus();
  
  // Initialize with cached value if available
  const [smsEnabled, setSmsEnabled] = useState<boolean>(cachedStatus !== null ? cachedStatus : false);
  const [usageData, setUsageData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [initialized, setInitialized] = useState(false);

  // Get organisation ID from user profile
  const organisationId = user?.organisationId;

  // Helper function to check if user should have SMS access
  const shouldLoadSmsData = useCallback(() => {
    if (!user || !user.role) return false;
    
    // Skip SMS data loading for onboarding users
    const onboardingRoles = [UserRole.NEW_SIGN_UP, UserRole.IN_PROGRESS];
    return !onboardingRoles.includes(user.role as UserRole);
  }, [user]);

  // Fetch SMS status with improved caching and debouncing
  const fetchSmsStatus = useCallback(async (forceRefresh = false) => {
    if (!organisationId) return;
    
    // Check if user should have SMS access
    if (!shouldLoadSmsData()) {
      console.log('SmsBillingContext: Skipping SMS status fetch - user role does not have SMS access');
      return;
    }
    
    // Prevent duplicate API calls by implementing rate limiting
    const now = Date.now();
    const MIN_FETCH_INTERVAL = 5000; // 5 seconds between status checks
    
    // Skip if we checked recently and this isn't a forced refresh
    if (!forceRefresh && now - lastFetchTime < MIN_FETCH_INTERVAL) {
      console.log('SmsBillingContext: Skipping fetch - too soon since last check');
      return;
    }
    
    // Skip if another fetch is already in progress
    if (isFetchingStatus) {
      console.log('SmsBillingContext: Skipping fetch - already in progress');
      return;
    }
    
    isFetchingStatus = true;
    setLoading(true);
    setError(null);
    
    try {
      console.log('SmsBillingContext: Fetching SMS status for organisation', organisationId);
      setLastFetchTime(now);
      globalLastFetchTime = now;
      
      const result = await checkSmsStatus(organisationId);
      setSmsEnabled(result.enabled);
      
      // Cache the result to localStorage for faster startup next time
      try {
        localStorage.setItem('sms_status_cache', JSON.stringify({
          enabled: result.enabled,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to cache SMS status:', e);
      }
    } catch (err) {
      console.error('Error fetching SMS status:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch SMS status');
      toast({
        title: "Failed to check SMS status",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      isFetchingStatus = false;
    }
  }, [organisationId, lastFetchTime, shouldLoadSmsData]);

  // Fetch SMS usage data with improved caching
  const fetchSmsUsage = useCallback(async (forceRefresh = false) => {
    if (!organisationId) return;
    
    // Check if user should have SMS access
    if (!shouldLoadSmsData()) {
      console.log('SmsBillingContext: Skipping SMS usage fetch - user role does not have SMS access');
      return;
    }
    
    try {
      console.log('SmsBillingContext: Fetching SMS usage for organization', organisationId);
      const result = await getSmsUsage(organisationId);
      if (result.success && result.usage) {
        setUsageData(result.usage);
      }
    } catch (err) {
      console.error('Error fetching SMS usage:', err);
      // Only show error toast for users who should have SMS access
      if (shouldLoadSmsData()) {
        toast({
          title: "Failed to load SMS usage data",
          description: err instanceof Error ? err.message : "Unknown error",
          variant: "destructive"
        });
      }
    }
  }, [organisationId, shouldLoadSmsData]);

  // Fetch data on component mount or when organisation changes with improved loading state management
  useEffect(() => {
    if (organisationId && dataInitialized && !initialized) {
      console.log('Auth data initialized, checking if SMS data should be loaded');
      
      // Check if user role allows SMS access
      if (!shouldLoadSmsData()) {
        console.log('User role does not require SMS data, skipping SMS loading');
        setInitialized(true);
        setLoading(false);
        return;
      }
      
      console.log('Loading SMS billing data for user with access');
      setLoading(true);
      
      // First, check if we have a cached value to use while fetching
      const cachedStatus = getCachedSmsStatus();
      if (cachedStatus !== null) {
        setSmsEnabled(cachedStatus);
        console.log('Using cached SMS status while fetching fresh data');
      }
      
      Promise.all([
        fetchSmsStatus(),
        fetchSmsUsage()
      ]).then(() => {
        console.log('SMS billing data loaded');
        setInitialized(true);
        setLoading(false);
      }).catch(error => {
        console.error('Error loading SMS billing data:', error);
        setLoading(false);
      });
    } else if (!organisationId) {
      setLoading(false);
    }

    // Subscribe to localStorage events to coordinate between multiple instances
    const handleStorageChange = (e) => {
      if (e.key === 'sms_status_refreshed') {
        const data = JSON.parse(e.newValue || '{}');
        if (data.timestamp > globalLastFetchTime) {
          globalLastFetchTime = data.timestamp;
          setSmsEnabled(data.enabled);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [organisationId, fetchSmsStatus, fetchSmsUsage, dataInitialized, shouldLoadSmsData]);

  // Enable SMS feature with cache invalidation
  const enableSms = async () => {
    if (!organisationId) {
      toast({
        title: "Organisation required",
        description: "No organisation ID found in your profile",
        variant: "destructive"
      });
      return;
    }
    
    // Check if user has permission to enable SMS
    if (!shouldLoadSmsData()) {
      toast({
        title: "Access denied",
        description: "You don't have permission to enable SMS features. Please complete your onboarding first.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await enableSmsFeature(organisationId);
      if (result.success) {
        setSmsEnabled(true);
        
        // Invalidate caches
        invalidateCache(`sms-status-${organisationId}`);
        invalidateCache(`sms-usage-${organisationId}`);
        
        // Notify other tabs/components using localStorage
        localStorage.setItem('sms_status_refreshed', JSON.stringify({
          timestamp: Date.now(),
          enabled: true
        }));
        
        toast({
          title: "SMS enabled",
          description: "SMS feature has been enabled for your organisation",
        });
        
        // Refresh usage data
        fetchSmsUsage(true);
      }
    } catch (err) {
      console.error('Error enabling SMS:', err);
      toast({
        title: "Failed to enable SMS",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Disable SMS feature with cache invalidation
  const disableSms = async () => {
    if (!organisationId) return;
    
    // Check if user has permission to disable SMS
    if (!shouldLoadSmsData()) {
      toast({
        title: "Access denied",
        description: "You don't have permission to disable SMS features. Please complete your onboarding first.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await disableSmsFeature(organisationId);
      if (result.success) {
        setSmsEnabled(false);
        
        // Invalidate caches
        invalidateCache(`sms-status-${organisationId}`);
        invalidateCache(`sms-usage-${organisationId}`);
        
        // Notify other tabs/components
        localStorage.setItem('sms_status_refreshed', JSON.stringify({
          timestamp: Date.now(),
          enabled: false
        }));
        
        toast({
          title: "SMS disabled",
          description: "SMS feature has been disabled for your organisation",
        });
        
        // Refresh usage data with force refresh
        fetchSmsUsage(true);
      }
    } catch (err) {
      console.error('Error disabling SMS:', err);
      toast({
        title: "Failed to disable SMS",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh both status and usage with proper cache invalidation
  const refreshData = async () => {
    if (!organisationId) return;
    
    // Check if user has permission to refresh SMS data
    if (!shouldLoadSmsData()) {
      console.log('User role does not have SMS access, skipping refresh');
      return;
    }
    
    // Invalidate related caches
    invalidateCacheByPrefix(`sms-status-${organisationId}`);
    invalidateCacheByPrefix(`sms-usage-${organisationId}`);
    
    // Force refresh of data
    await fetchSmsStatus(true);
    await fetchSmsUsage(true);
  };

  return (
    <SmsBillingContext.Provider
      value={{
        organisationId,
        smsEnabled,
        usageData,
        loading: loading || (!!user && !dataInitialized), // Keep loading true until auth data is initialized
        error,
        enableSms,
        disableSms,
        refreshData
      }}
    >
      {children}
    </SmsBillingContext.Provider>
  );
};

// Custom hook to use the context
export const useSmsBilling = () => {
  const context = useContext(SmsBillingContext);
  if (context === undefined) {
    throw new Error('useSmsBilling must be used within a SmsBillingProvider');
  }
  return context;
};
